import { ChatCircleDots, InstagramLogo, MinusCircle, Plus, YoutubeLogo } from "@phosphor-icons/react";
import { getPincheIncompleteMessage } from "../lib/incompleteRecipeMessages";

export const SocialInputFallback = ({
  info,
  creatorText,
  transcript,
  ingredients,
  procedure,
  busy,
  onCreatorText,
  onTranscript,
  onIngredients,
  onProcedure,
  onRetry,
  onUpload,
}) => {
  const source = info.source || info.youtube || {};
  const platform = source.platform || (info.youtube ? "youtube" : "instagram");
  const platformName = platform === "instagram" ? "Instagram" : "YouTube";
  const testPrefix = platform === "instagram" ? "instagram" : "youtube";
  const pipeline = info.pipeline_state || {};
  const completeIngredients = ingredients.filter((item) => item.name.trim() && item.quantity.trim());
  const needsIngredients = pipeline.ingredients_sufficient === false
    || (!info.pipeline_state && (info.code?.endsWith("ingredients_required") || info.code?.endsWith("more_info_required")));
  const needsProcedure = pipeline.procedure_sufficient === false
    || (!info.pipeline_state && (info.code?.endsWith("procedure_required") || info.code?.endsWith("more_info_required")));
  const audioDownloaded = pipeline.audio_downloaded === true;
  const transcriptAvailable = pipeline.transcript_available === true;
  const hasTextSource = transcript.trim() || creatorText.trim() || procedure.trim();
  const ingredientsReady = !needsIngredients || completeIngredients.length > 0 || Boolean(creatorText.trim());
  const procedureReady = !needsProcedure || Boolean(procedure.trim() || creatorText.trim() || (!transcriptAvailable && transcript.trim()));
  const canRetry = Boolean(completeIngredients.length > 0 || hasTextSource) && ingredientsReady && procedureReady;
  const pincheMessage = getPincheIncompleteMessage(info.code, pipeline, info.message);
  const creatorLabel = needsIngredients && !needsProcedure
    ? "Texto del creador con ingredientes"
    : needsProcedure && !needsIngredients
      ? "Pasos publicados por el creador"
      : "Texto o comentario del creador";
  const creatorPlaceholder = needsIngredients && !needsProcedure
    ? "Pega únicamente los ingredientes y cantidades publicados por el creador…"
    : needsProcedure && !needsIngredients
      ? "Pega únicamente los pasos publicados por el creador…"
      : "Pega los ingredientes, cantidades o pasos que todavía falten…";
  const continueLabel = needsIngredients && !needsProcedure
    ? "Continuar con estos ingredientes"
    : needsProcedure && !needsIngredients
      ? "Continuar con estos pasos"
      : "Continuar con estos datos";

  const updateIngredient = (index, field, value) => {
    onIngredients(ingredients.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [field]: value } : item
    )));
  };

  const addIngredient = () => {
    onIngredients([...ingredients, { name: "", quantity: "", notes: "" }]);
  };

  const removeIngredient = (index) => {
    const next = ingredients.filter((_, itemIndex) => itemIndex !== index);
    onIngredients(next.length ? next : [{ name: "", quantity: "", notes: "" }]);
  };

  const askPinche = () => {
    const ingredientSummary = completeIngredients
      .map((item) => `${item.quantity} ${item.name}`)
      .join("; ");
    const ingredientsPreserved = completeIngredients.length > 0 || pipeline.ingredients_sufficient === true;
    const displayMessage = needsIngredients && !needsProcedure
      ? "Ayúdame a completar solo los ingredientes y cantidades que faltan."
      : needsProcedure && !needsIngredients
        ? "Ayúdame a completar solo los pasos del procedimiento que faltan."
        : "Ayúdame a completar únicamente los datos que todavía faltan.";
    window.dispatchEvent(new CustomEvent(`mizeta:pinche-${platform}-help`, {
      detail: {
        displayMessage,
        contextMessage: [
          `[CONTEXTO_${platform === "instagram" ? "INSTAGRAM" : "YOUTUBE"}_IMPORTACION]`,
          `INGREDIENTES_YA_INTRODUCIDOS: ${ingredientsPreserved ? "sí" : "no"}`,
          ingredientSummary ? `INGREDIENTES_CONSERVADOS: ${ingredientSummary}` : "INGREDIENTES_CONSERVADOS: ninguno",
          `METADATA_DISPONIBLE: ${pipeline.metadata_available ? "sí" : "no"}`,
          `AUDIO_DESCARGADO: ${audioDownloaded ? "sí" : "no"}`,
          `TRANSCRIPCION_DISPONIBLE: ${transcriptAvailable ? "sí" : "no"}`,
          `INGREDIENTES_SUFICIENTES: ${pipeline.ingredients_sufficient ? "sí" : "no"}`,
          `PROCEDIMIENTO_SUFICIENTE: ${pipeline.procedure_sufficient ? "sí" : "no"}`,
          "SOURCE_URL_DISPONIBLE: sí",
          `SUBTITULOS_DISPONIBLES: ${source.captions_available ? "sí" : "no consta"}`,
          `Pide únicamente los datos que faltan para completar la receta de ${platformName}.`,
        ].join("\n"),
        importContext: {
          platform,
          code: info.code || `${platform}_more_info_required`,
          metadata_available: pipeline.metadata_available === true,
          audio_downloaded: audioDownloaded,
          transcript_available: transcriptAvailable,
          ingredients_sufficient: pipeline.ingredients_sufficient === true,
          procedure_sufficient: pipeline.procedure_sufficient === true,
          accepted_ingredients_count: Math.max(
            completeIngredients.length,
            Array.isArray(pipeline.accepted_ingredients) ? pipeline.accepted_ingredients.length : 0,
          ),
          accepted_procedure: Boolean(pipeline.accepted_procedure || info.accepted_procedure),
          source_url_available: Boolean(source.url),
        },
      },
    }));
  };

  return (
    <section className="border border-[hsl(var(--border))] bg-white rounded-2xl p-5 sm:p-6" data-testid={`${testPrefix}-input-fallback`}>
      <div className="flex gap-4 items-start">
        {source.thumbnail && (
          <img src={source.thumbnail} alt={`Miniatura del vídeo de ${platformName}`} className="w-24 aspect-video object-cover rounded-lg shrink-0" data-testid={`${testPrefix}-fallback-thumbnail`} />
        )}
        <div className="min-w-0">
          <p className="font-semibold leading-snug" data-testid={`${testPrefix}-fallback-title`}>{source.title}</p>
          {(source.creator || source.channel) && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1" data-testid={`${testPrefix}-fallback-channel`}>{source.creator || source.channel}</p>}
          <a href={source.url} target="_blank" rel="noreferrer" className="text-xs text-[hsl(var(--primary))] inline-flex items-center gap-1 mt-2" data-testid={`${testPrefix}-fallback-original-link`}>
            Abrir vídeo original {platform === "instagram" ? <InstagramLogo size={13} /> : <YoutubeLogo size={13} />}
          </a>
        </div>
      </div>

      <div className="mt-5 flex gap-3 items-start" data-testid={`${testPrefix}-pinche-guidance`}>
        <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary))]/12 flex items-center justify-center shrink-0" aria-hidden="true">
          <ChatCircleDots size={17} weight="fill" className="text-[hsl(var(--primary))]" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-[hsl(var(--primary))]">Pinche</p>
          <p className="mt-1 text-sm leading-relaxed text-[hsl(var(--muted-foreground))]" data-testid={`${testPrefix}-fallback-message`}>{pincheMessage}</p>
        </div>
      </div>
      {platform === "youtube" && source.captions_available && (
        <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]" data-testid="youtube-captions-notice">
          YouTube indica que hay subtítulos. Puedes copiar “Mostrar transcripción” y pegarla aquí.
        </p>
      )}
      <button type="button" onClick={askPinche} disabled={busy} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[hsl(var(--primary))]" data-testid={`${testPrefix}-ask-pinche-button`}>
        <ChatCircleDots size={18} /> Pedir ayuda a Pinche
      </button>

      <label className="block mt-6">
        <span className="block text-xs font-semibold uppercase text-[hsl(var(--muted-foreground))] mb-2" data-testid={`${testPrefix}-creator-text-label`}>{creatorLabel}</span>
        <textarea value={creatorText} onChange={(event) => onCreatorText(event.target.value)} rows={4} disabled={busy} placeholder={creatorPlaceholder} className="w-full border border-[hsl(var(--border))] rounded-xl p-3 text-sm focus:outline-none focus:border-[hsl(var(--primary))]" data-testid={`${testPrefix}-creator-text-input`} />
      </label>

      {needsIngredients && <fieldset className="mt-6" data-testid={`${testPrefix}-manual-ingredients-grid`}>
        <legend className="text-xs font-semibold uppercase text-[hsl(var(--muted-foreground))]">Ingredientes aportados por ti</legend>
        <div className="mt-3 grid gap-3">
          {ingredients.map((item, index) => (
            <div key={index} className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(8rem,0.45fr)_2.5rem] gap-2 items-center">
              <input value={item.name} onChange={(event) => updateIngredient(index, "name", event.target.value)} disabled={busy} placeholder="Ingrediente" className="w-full border border-[hsl(var(--border))] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--primary))]" data-testid={`${testPrefix}-ingredient-name-${index}`} />
              <input value={item.quantity} onChange={(event) => updateIngredient(index, "quantity", event.target.value)} disabled={busy} placeholder="Cantidad/peso" className="w-full border border-[hsl(var(--border))] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--primary))]" data-testid={`${testPrefix}-ingredient-quantity-${index}`} />
              <button type="button" onClick={() => removeIngredient(index)} disabled={busy} aria-label={`Eliminar ingrediente ${index + 1}`} className="w-10 h-10 inline-flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]" data-testid={`${testPrefix}-remove-ingredient-${index}`}>
                <MinusCircle size={20} />
              </button>
            </div>
          ))}
        </div>
        <button type="button" onClick={addIngredient} disabled={busy} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--primary))]" data-testid={`${testPrefix}-add-ingredient-button`}>
          <Plus size={15} /> Añadir ingrediente
        </button>
      </fieldset>}

      {needsProcedure && <label className="block mt-6">
        <span className="block text-xs font-semibold uppercase text-[hsl(var(--muted-foreground))] mb-2">Procedimiento o pasos</span>
        <textarea value={procedure} onChange={(event) => onProcedure(event.target.value)} rows={4} disabled={busy} placeholder="Explica cómo preparar la receta sin añadir ingredientes nuevos…" className={`w-full border rounded-xl p-3 text-sm focus:outline-none focus:border-[hsl(var(--primary))] ${needsProcedure ? "border-[hsl(var(--primary))]" : "border-[hsl(var(--border))]"}`} data-testid={`${testPrefix}-procedure-input`} />
      </label>}

      {needsProcedure && !transcriptAvailable && <label className="block mt-4">
        <span className="block text-xs font-semibold uppercase text-[hsl(var(--muted-foreground))] mb-2">Pegar transcripción</span>
        <textarea value={transcript} onChange={(event) => onTranscript(event.target.value)} rows={4} disabled={busy} placeholder={`Pega aquí una transcripción o los pasos disponibles en ${platformName}…`} className="w-full border border-[hsl(var(--border))] rounded-xl p-3 text-sm focus:outline-none focus:border-[hsl(var(--primary))]" data-testid={`${testPrefix}-transcript-input`} />
      </label>}

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <button type="button" onClick={onRetry} disabled={busy || !canRetry || !procedureReady} className="px-5 py-3 rounded-full bg-[hsl(var(--primary))] text-white text-sm font-semibold disabled:opacity-50 btn-press" data-testid={`${testPrefix}-create-from-ingredients-button`}>
          {continueLabel}
        </button>
        {!audioDownloaded && <button type="button" onClick={onUpload} disabled={busy} className="px-5 py-3 rounded-full border border-[hsl(var(--border))] text-sm font-semibold btn-press" data-testid={`${testPrefix}-authorized-upload-button`}>
          Subir archivo propio/autorizado
        </button>}
      </div>
    </section>
  );
};